<script setup>
import { ref, computed, nextTick, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { formatTime } from '@/utils/datetime'
import { User, Bot, Loader2, Info, ChevronDown, ChevronUp } from 'lucide-vue-next'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

// Configure marked options
marked.setOptions({
  breaks: true,
  gfm: true,
  smartLists: true,
  smartypants: true
})

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'] || { tz: 'UTC' })
const currentMember = computed(() => store.getters['user/getMember'])

const props = defineProps({
  messages: {
    type: Array,
    default: () => []
  },
  isGenerating: {
    type: Boolean,
    default: false
  }
})

// Sort messages by created_at to ensure correct ordering
const sortedMessages = computed(() => {
  return [...props.messages].sort((a, b) => {
    return new Date(a.created_at) - new Date(b.created_at)
  })
})

const messagesEndRef = ref(null)

// Auto-scroll to bottom
const scrollToBottom = async () => {
  await nextTick()
  if (messagesEndRef.value) {
    messagesEndRef.value.scrollIntoView({ behavior: 'smooth' })
  }
}

// Watch messages and scroll
watch(() => sortedMessages.value, () => {
  scrollToBottom()
}, { deep: true })

onMounted(() => {
  scrollToBottom()
})

// Extract content from message (handles both string and object format)
const getMessageContent = (message) => {
  if (!message.content) return ''

  // If content is a string, return it directly
  if (typeof message.content === 'string') {
    return message.content
  }

  // If content is an object with a content field, extract it
  if (typeof message.content === 'object' && message.content.content) {
    return message.content.content
  }

  // Otherwise, stringify the object
  return JSON.stringify(message.content)
}

// Render markdown content for assistant messages
const renderMarkdown = (content, isStreaming = false) => {
  if (!content) return ''

  // During streaming, render as plain text to avoid markdown parsing issues
  // (e.g., incomplete headings rendering as H1)
  if (isStreaming) {
    // Escape HTML and convert newlines to <br>
    const escaped = content
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br>')
    return escaped
  }

  const htmlContent = marked.parse(content)
  return DOMPurify.sanitize(htmlContent)
}

// Clean model name by removing date suffix
const cleanModelName = (modelName) => {
  if (!modelName) return 'Unknown Model'

  // Remove date suffix (e.g., "claude-haiku-4-5-20251001" -> "Claude Haiku 4.5")
  // Match pattern: claude-{model}-{version}-{date}
  const match = modelName.match(/^(claude)[-_]([a-z]+)[-_](\d+)[-_](\d+)/)
  if (match) {
    const [, , model, major, minor] = match
    const modelName = model.charAt(0).toUpperCase() + model.slice(1)
    return `Claude ${modelName.replace(/-/g, ' ')} ${major}.${minor}`
  }
}

// Expandable metadata state
const expandedMessages = ref(new Set())

const toggleMetadata = (messageCode) => {
  if (expandedMessages.value.has(messageCode)) {
    expandedMessages.value.delete(messageCode)
  } else {
    expandedMessages.value.add(messageCode)
  }
  // Trigger reactivity
  expandedMessages.value = new Set(expandedMessages.value)
}
</script>

<template>
  <div class="p-4 space-y-4">
    <!-- Empty State -->
    <div v-if="sortedMessages.length === 0" class="flex items-center justify-center h-full text-muted-foreground">
      <div class="text-center">
        <Bot class="h-12 w-12 mx-auto mb-4 opacity-30" />
        <p class="text-sm">How I can assist you today?</p>
      </div>
    </div>

    <!-- Messages -->
    <template v-for="message in sortedMessages" :key="message.message_code">
      <!-- User Message -->
      <div v-if="message.role === 'user'" class="flex justify-end">
        <div class="flex items-start gap-2 max-w-[80%]">
          <div class="flex-1">
            <div class="bg-blue-600 text-white rounded-lg px-4 py-2">
              <p class="text-sm whitespace-pre-wrap">{{ getMessageContent(message) }}</p>
            </div>
            <div class="text-xs text-muted-foreground mt-1 text-right">
              {{ formatTime(message.created_at, settings.tz, 'HH:mm') }}
            </div>
          </div>
          <div class="flex-shrink-0">
            <Avatar class="w-8 h-8">
              <AvatarImage
                v-if="currentMember?.avatar_url"
                :src="currentMember.avatar_url"
                :alt="currentMember?.display_name || 'User'"
              />
              <AvatarFallback>
                <User class="h-4 w-4 text-blue-600" />
              </AvatarFallback>
            </Avatar>
          </div>
        </div>
      </div>

      <!-- Assistant Message -->
      <div v-else-if="message.role === 'assistant'" class="flex justify-start">
        <div class="flex items-start gap-2 max-w-[80%]">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
              <Bot class="h-4 w-4 text-muted-foreground" />
            </div>
          </div>
          <div class="flex-1">
            <div class="bg-muted/50 border border-border rounded-lg px-4 py-2 shadow-sm">
              <div class="prose prose-sm dark:prose-invert max-w-none text-sm text-foreground" v-html="renderMarkdown(getMessageContent(message), message.message_code === 'streaming')"></div>
            </div>
            <div class="text-xs text-muted-foreground mt-1">
              <div class="flex items-center justify-between gap-2">
                <span>{{ formatTime(message.created_at, settings.tz, 'HH:mm') }}</span>
                <button
                  v-if="message.message_metadata"
                  @click="toggleMetadata(message.message_code)"
                  class="ml-1 text-muted-foreground/50 hover:text-muted-foreground transition-colors"
                  :title="expandedMessages.has(message.message_code) ? 'Hide details' : 'Show details'"
                >
                  <Info class="h-3 w-3" />
                </button>
              </div>

              <!-- Expanded metadata details -->
              <div
                v-if="message.message_metadata && expandedMessages.has(message.message_code)"
                class="mt-2 p-2 bg-muted rounded border border-border space-y-1"
              >
                <div class="flex items-center justify-between">
                  <span class="text-muted-foreground/70">Model:</span>
                  <span class="font-mono text-xs">{{ cleanModelName(message.message_metadata.model_name) }}</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-muted-foreground/70">Input tokens:</span>
                  <span class="font-mono">{{ message.message_metadata.input_tokens?.toLocaleString() || 0 }}</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-muted-foreground/70">Output tokens:</span>
                  <span class="font-mono">{{ message.message_metadata.output_tokens?.toLocaleString() || 0 }}</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-muted-foreground/70">Total tokens:</span>
                  <span class="font-mono">{{ ((message.message_metadata.input_tokens || 0) + (message.message_metadata.output_tokens || 0)).toLocaleString() }}</span>
                </div>
                <div class="flex items-center justify-between">
                  <span class="text-muted-foreground/70">Generation time:</span>
                  <span class="font-mono">{{ (message.message_metadata.generation_time_ms / 1000).toFixed(2) }}s</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Scroll anchor -->
    <div ref="messagesEndRef"></div>
  </div>
</template>
